import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Search, User, ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Acasă" },
  { to: "/produse", label: "Produse" },
  { to: "/branduri", label: "Branduri" },
  { to: "/despre-noi", label: "Despre noi" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const DARK_HERO = ["/", "/despre-noi"];
  const lightText = !scrolled && DARK_HERO.includes(pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/produse?q=${encodeURIComponent(query)}`);
    setSearchOpen(false);
    setMobile(false);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-cream/80 backdrop-blur-xl border-b border-line" : "bg-transparent"
      } ${lightText ? "text-cream" : "text-ink"}`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <span className="font-display text-2xl md:text-[1.7rem] leading-none tracking-tight">
            Coffee Cup <span className={lightText ? "text-sand" : "text-roast"}>Supply</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label}`}
              className={({ isActive }) =>
                `overline transition-colors duration-300 hover:text-roast ${
                  isActive ? (lightText ? "text-sand" : "text-roast") : lightText ? "text-cream/85" : "text-ink/70"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 md:gap-3">
          <button
            onClick={() => setSearchOpen((s) => !s)}
            className="p-2 hover:text-roast transition-colors"
            data-testid="search-toggle"
            aria-label="Caută"
          >
            <Search size={20} />
          </button>
          <Link
            to={user ? "/cont" : "/autentificare"}
            className="p-2 hover:text-roast transition-colors"
            data-testid="account-link"
            aria-label="Cont"
          >
            <User size={20} />
          </Link>
          <button
            onClick={() => setOpen(true)}
            className="relative p-2 hover:text-roast transition-colors"
            data-testid="cart-toggle"
            aria-label="Coș"
          >
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-0.5 -right-0.5 h-5 w-5 grid place-items-center rounded-full bg-roast text-cream text-[10px] font-semibold"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="lg:hidden p-2"
            onClick={() => setMobile((m) => !m)}
            data-testid="mobile-menu-toggle"
            aria-label="Meniu"
          >
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-line bg-cream/95 backdrop-blur-xl px-5 md:px-10 py-4 text-ink">
          <form onSubmit={submitSearch} className="max-w-[1400px] mx-auto flex items-center gap-3">
            <Search size={18} className="text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Caută cafea sau brand..."
              data-testid="search-input"
              className="flex-1 bg-transparent outline-none text-lg placeholder:text-muted-foreground"
            />
            <button type="submit" className="overline text-roast" data-testid="search-submit">
              Caută
            </button>
          </form>
        </div>
      )}

      {mobile && (
        <div className="lg:hidden border-t border-line bg-cream px-5 py-6 flex flex-col gap-4 text-ink">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setMobile(false)}
              className="font-display text-3xl"
              data-testid={`mobile-nav-${n.label}`}
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
